import { useEffect, useRef } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import {
  type WorkspaceView,
  closeWorkspace,
  currentWorkspace,
  minimizeWorkspace,
  openWorkspace,
  useWorkspaceSession,
  workspaceHasUnfinishedWork
} from "./workspace-store.ts"

const urlOf = (envelopeId: string, view: WorkspaceView) =>
  view === "sign"
    ? RoutesPaths.esign.sign(envelopeId)
    : RoutesPaths.esign.request(envelopeId)

/**
 * A request's own address — a link in an email, a bookmark, a reload — opens
 * it in the workspace over the page behind. Leaving the address (Back) closes
 * it, or puts it aside when signing is under way; closing it leaves the address.
 */
export function OpenInWorkspace({ view }: { view: WorkspaceView }) {
  const { id = "" } = useParams()
  const navigate = useNavigate()
  const { search } = useLocation()
  const list = `${RoutesPaths.signatureRequests}${search}`
  // The query is the list's, carried along; a change to it never reopens anything.
  const carried = useRef(search)
  carried.current = search
  const session = useWorkspaceSession()
  const opened = useRef<string | null>(null)

  useEffect(() => {
    if (!id) return
    let abandoned = false
    void openWorkspace({ envelopeId: id, view }).then((ok) => {
      if (abandoned) return
      if (ok) {
        opened.current = id
        return
      }
      // Kept the request already open: the address goes back to it.
      if (opened.current)
        navigate(`${urlOf(opened.current, view)}${carried.current}`, {
          replace: true
        })
      else
        navigate(`${RoutesPaths.signatureRequests}${carried.current}`, {
          replace: true
        })
    })
    return () => {
      abandoned = true
    }
  }, [id, view, navigate])

  // Closed from inside: the address stops naming the request.
  const closedHere = opened.current === id && !session
  useEffect(() => {
    if (closedHere) navigate(list, { replace: true })
  }, [closedHere, navigate, list])

  // The address was left (Back, or a link elsewhere) while the request was in front.
  const latestId = useRef(id)
  latestId.current = id
  useEffect(
    () => () => {
      const now = currentWorkspace()
      if (
        !now ||
        now.minimized ||
        now.envelopeId !== latestId.current ||
        opened.current !== latestId.current
      )
        return
      if (workspaceHasUnfinishedWork()) minimizeWorkspace()
      else void closeWorkspace({ force: true })
    },
    []
  )

  return null
}
